import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, throwError, timer } from 'rxjs';
import { switchMap, tap, catchError, of, combineLatest } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Notification, NotificationListResponse, MarkNotificationReadRequest, MarkAllReadRequest } from '../models/notification.model';
import { Store } from '@ngxs/store';
import { DealerState } from '../state/DealerState';
import { NotificationPreferencesService } from './notification-preferences.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly notificationPreferencesService = inject(NotificationPreferencesService);

  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  private readonly MAX_BADGE_COUNT = 99;
  private readonly pollInterval = environment.notificationPollIntervalMs;
  private readonly readPendingTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
  private activeDealerId: string | undefined;

  constructor() {
    if (!Number.isInteger(this.pollInterval) || this.pollInterval <= 0 || this.pollInterval > 2147483647) {
      throw new Error('environment.notificationPollIntervalMs must be a positive 32-bit integer (milliseconds)');
    }

    this.store.select(DealerState.currentDealer).subscribe((dealer) => {
      const dealerId = dealer?.id;

      if (!dealerId) {
        this.clearSessionState();
        this.activeDealerId = undefined;
        return;
      }

      if (this.activeDealerId && this.activeDealerId !== dealerId) {
        this.clearSessionState();
      }

      if (this.activeDealerId !== dealerId) {
        this.activeDealerId = dealerId;
      }
    });

    this.startPolling();
  }

  /**
   * DealerState is not registered in every NGXS test module, in which case the
   * selector receives an undefined slice and throws. Notifications are not
   * critical enough to break construction of whatever injected this service.
   */
  private getCurrentDealerId(): string | undefined {
    try {
      return this.store.selectSnapshot(DealerState.currentDealer)?.id;
    } catch {
      return undefined;
    }
  }

  /**
   * The global feature flag controls bell availability; selected resource
   * types control which notifications may populate it. Any preference change
   * recreates the timer, so enabling a type fetches immediately and disabling
   * one removes its currently visible entries before another response arrives.
   */
  private startPolling(): void {
    combineLatest([
      this.notificationPreferencesService.notificationsFeatureEnabled$,
      this.notificationPreferencesService.enabledResourceTypes$
    ])
      .pipe(
        tap(([featureEnabled, resourceTypes]) => {
          if (!featureEnabled || resourceTypes.length === 0) {
            this.clearSessionState();
            return;
          }
          this.filterVisibleNotifications(resourceTypes);
        }),
        switchMap(([featureEnabled, resourceTypes]) => (featureEnabled && resourceTypes.length > 0 ? timer(0, this.pollInterval) : EMPTY)),
        switchMap(() => {
          const dealerId = this.getCurrentDealerId();
          return dealerId ? this.loadNotifications(dealerId) : of(null);
        })
      )
      .subscribe();
  }

  /**
   * Refresh the current dealer's notification snapshot from the backend.
   * The backend may now return the current state for the dealer, including
   * read/dismissed notifications with `isSeen` values, so the UI should treat
   * the response as the authoritative state instead of assuming unread-only.
   */
  loadNotifications(dealerId: string): Observable<NotificationListResponse> {
    return this.http.get<NotificationListResponse>(`${environment.backOfficeUrl}api/notifications/${dealerId}`).pipe(
      tap((response) => {
        const notifications = this.filterNotificationsByEnabledResourceType(this.normalizeNotificationList(response));
        this.notificationsSubject.next(notifications);
        this.updateUnreadCountFromList(notifications);
      }),
      catchError((error) => {
        console.error('Failed to load notifications:', error);
        this.notificationsSubject.next([]);
        this.updateUnreadCount(0);
        return of({ notifications: this.notificationsSubject.value, unreadCount: this.unreadCountSubject.value });
      })
    );
  }

  markAsRead(notificationId: string, channel: 'SHOPFLOOR' | 'BACKOFFICE'): Observable<void> {
    const dealerId = this.getCurrentDealerId();

    const markLocalAsRead = () => {
      const current = this.notificationsSubject.value;
      const updated = current.map((n) =>
        n.notificationId === notificationId ? { ...n, isSeen: true, seenAt: new Date().toISOString(), seenChannel: channel } : n
      );
      this.notificationsSubject.next(updated);
      this.updateUnreadCountFromList(updated);
      this.scheduleRemoval(notificationId, 10000);
    };

    if (!dealerId) {
      markLocalAsRead();
      return of(undefined);
    }

    const request: MarkNotificationReadRequest = { notificationId, channel };
    return this.http.post<void>(`${environment.backOfficeUrl}api/notifications/${dealerId}/mark-read`, request).pipe(
      tap(() => markLocalAsRead()),
      catchError((error) => {
        console.error('Failed to mark notification as read:', error);
        return throwError(() => error);
      })
    );
  }

  dismissAll(): void {
    const dealerId = this.getCurrentDealerId();
    const current = this.notificationsSubject.value;
    const unread = current.filter((n) => !n.isSeen);

    if (unread.length === 0) {
      return;
    }

    const clearLocally = () => {
      current.forEach((notification) => this.clearRemovalTimer(notification.notificationId));
      this.clearSessionState();
    };

    if (!dealerId) {
      clearLocally();
      return;
    }

    const request: MarkAllReadRequest = { channel: 'BACKOFFICE' };
    this.http.post<void>(`${environment.backOfficeUrl}api/notifications/${dealerId}/mark-all-read`, request).subscribe({
      next: () => clearLocally(),
      error: (error) => console.error('Failed to dismiss all notifications on server: ', error)
    });
  }

  clearSessionState(): void {
    this.readPendingTimeouts.forEach((timer) => clearTimeout(timer));
    this.readPendingTimeouts.clear();
    this.notificationsSubject.next([]);
    this.updateUnreadCount(0);
  }

  getUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  private updateUnreadCount(count: number): void {
    this.unreadCountSubject.next(count);
  }

  private updateUnreadCountFromList(list: Notification[]): void {
    this.updateUnreadCount(list.filter((n) => !n.isSeen).length);
  }

  private filterVisibleNotifications(enabledResourceTypes: readonly string[]): void {
    const visibleNotifications = this.notificationsSubject.value.filter((notification) =>
      enabledResourceTypes.includes(notification.resourceType)
    );
    this.notificationsSubject.next(visibleNotifications);
    this.updateUnreadCountFromList(visibleNotifications);
  }

  private filterNotificationsByEnabledResourceType(notifications: Notification[]): Notification[] {
    return notifications.filter((notification) => this.notificationPreferencesService.isResourceTypeEnabled(notification.resourceType));
  }

  private normalizeNotificationList(response: Partial<NotificationListResponse> | Notification[] | undefined): Notification[] {
    const list = Array.isArray(response)
      ? response
      : (response as Partial<NotificationListResponse>)?.notifications ?? (response as any)?.items ?? [];

    return (list as Notification[])
      .filter((notification) => notification && typeof notification.notificationId !== 'undefined')
      .map((notification) => ({
        ...notification,
        isSeen: !!notification.isSeen
      }));
  }

  private scheduleRemoval(notificationId: string, delayMs: number): void {
    this.clearRemovalTimer(notificationId);

    const timeout = setTimeout(() => {
      const current = this.notificationsSubject.value.filter((n) => n.notificationId !== notificationId);
      this.notificationsSubject.next(current);
      this.updateUnreadCountFromList(current);
      this.readPendingTimeouts.delete(notificationId);
    }, delayMs);

    this.readPendingTimeouts.set(notificationId, timeout);
  }

  private clearRemovalTimer(notificationId: string): void {
    const existingTimer = this.readPendingTimeouts.get(notificationId);
    if (existingTimer) {
      clearTimeout(existingTimer);
      this.readPendingTimeouts.delete(notificationId);
    }
  }

  getDisplayCount(): string {
    const count = this.getUnreadCount();
    return count > this.MAX_BADGE_COUNT ? `${this.MAX_BADGE_COUNT}+` : count.toString();
  }
}
